import React, { useState, useEffect } from 'react';
import './QuestionAnswer.css';
import { searchRedditPosts } from '../utils/redditApi';

function QuestionAnswer({ question }) {
  const [activeTab, setActiveTab] = useState('factors');
  const [redditPosts, setRedditPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchRedditContent = async () => {
      if (!question) return;
      
      setIsLoading(true);
      setError(null);
      
      try {
        const posts = await searchRedditPosts(question);
        setRedditPosts(posts);
      } catch (err) {
        setError('Failed to fetch Reddit posts. Please try again later.');
        console.error('Error:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRedditContent();
  }, [question]);

  const tabs = [
    { id: 'factors', label: 'Key Factors', icon: '🎯' },
    { id: 'forYou', label: 'For You', icon: '👤' },
    { id: 'nextSteps', label: 'Next Steps', icon: '📝' },
    { id: 'community', label: 'Community', icon: '👥' }
  ];

  const keyFactors = [
    {
      title: "Success Rates (but the right ones)",
      icon: "📊",
      content: "Ask: what's your success rate for people my age? Look it up on SART or CDC if the clinic is US-based. Ask: on average, how many eggs do women my age freeze here? How many turn into embryos?"
    },
    {
      title: "Transparent Pricing",
      icon: "💸",
      content: "What's included in the quote — meds, anesthesia, labs, storage? Are there hidden extras (like embryo creation or extra monitoring)? Some clinics share pricing openly (like Extend) — others… you have to dig"
    },
    {
      title: "Scheduling & Proximity",
      icon: "🕐",
      content: "Can you easily get to monitoring appointments before work? Do they do weekends? How packed is their schedule? During the stims phase, some went in 5+ times a week — short commutes mattered"
    },
    {
      title: "Doctor Energy & Communication",
      icon: "👩🏽‍⚕️",
      content: "Do you meet your doctor or mostly deal with nurses? Will you have the same doctor for your whole cycle? Do you feel rushed… or do they actually listen?"
    },
    {
      title: "Medication Protocol Clarity",
      icon: "💊",
      content: "Ask: what meds would they recommend for someone like you? Why that protocol? Even if you don't know all the science, thoughtful answers = thoughtful care. Bonus: knowing the meds early helps you estimate costs"
    }
  ];

  const communityPosts = [
    {
      id: 1,
      title: "My Experience Choosing a Clinic",
      content: "After visiting three different clinics, I finally found the right one. The key was finding a doctor who really listened to my concerns and explained everything clearly. Don't be afraid to shop around!",
      author: "FertilityWarrior",
      upvotes: 156,
      comments: 45
    },
    {
      id: 2,
      title: "Questions I Wish I Had Asked",
      content: "Make sure to ask about their lab quality controls, embryologist experience, and what happens if you need to cancel a cycle. These weren't obvious to me at first but made a huge difference.",
      author: "IVFJourney",
      upvotes: 234,
      comments: 78
    },
    {
      id: 3,
      title: "Red Flags vs Green Flags",
      content: "Green flags: transparent pricing, responsive staff, clear protocols. Red flags: pushy sales tactics, unclear cost breakdowns, dismissive of questions. Trust your gut!",
      author: "FertilityHope",
      upvotes: 189,
      comments: 67
    }
  ];

  return (
    <div className="qa-container">
      <h2 className="qa-title">Question: {question}</h2>

      <div className="tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span className="tab-icon">{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      <div className="tab-content">
        {activeTab === 'factors' && (
          <div className="content-section">
            <div className="factors-intro">
              <p>Here's how women actually made their decisions in the threads:</p>
              <ul>
                <li>60% dug into clinic stats — they wanted data and success rates (esp. from SART or CDC)</li>
                <li>50% went with what they could actually afford (full cost breakdowns mattered)</li>
                <li>40% needed a clinic that fit into their daily life — early AM appts, close to work, flexible timing</li>
                <li>30% based it on how the doctor made them feel — rushed vs. seen</li>
                <li>20% looked for a woman doctor they could trust and feel safe with</li>
              </ul>
              <p>So yeah, it was part research, part vibes, part logistics. Most didn't pick just the "top-rated" clinic — they picked the one that felt right for them.</p>
            </div>
            <div className="factors-list">
              {keyFactors.map((factor, index) => (
                <div key={index} className="factor-item">
                  <div className="factor-header">
                    <span className="factor-icon">{factor.icon}</span>
                    <h4>{factor.title}</h4>
                  </div>
                  <p>{factor.content}</p>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {activeTab === 'forYou' && (
          <div className="content-section">
            <div className="for-you-content">
              <p>
                You don't need to rush — at 30, you likely have a solid ovarian reserve, so take your time. Meet with a few clinics. Don't settle after one consult.
              </p>
              <p>
                Think about embryos vs eggs — since you have a partner, the embryo option might come up. Some clinics will push for it; ask them to walk you through pros, cons, and legal implications if you split.
              </p>
              <p>
                Use this time to compare how each clinic makes you feel — are they transparent? Rushed? Supportive? This process isn't just clinical — it's emotional. You deserve both facts and compassion.
              </p>
              <p>
                Treat it like dating — talk to 2–3 clinics. The one with the flashiest website isn't always the one that gets you.
              </p>
            </div>
          </div>
        )}

        {activeTab === 'nextSteps' && (
          <div className="content-section">
            <div className="next-steps-content">
              <div className="step-section">
                <h4>1. Pick 2–3 clinics to reach out to for consults</h4>
                <p>You don't have to know everything yet — just start the convo.</p>
                <ul>
                  <li>Use Google or SART.org to find clinics in your area</li>
                  <li>Look for ones with strong success rates for your age group</li>
                  <li>Bonus if they offer free consults or easy online booking</li>
                </ul>
              </div>

              <div className="step-section">
                <h4>2. Ask these 5 questions in your consults:</h4>
                <p>Save this, screenshot it, whatever works.</p>
                <ul>
                  <li>What's your egg retrieval success rate for someone my age?</li>
                  <li>What would my total cost be — meds, storage, and all?</li>
                  <li>Who will be managing my cycle day to day — one doctor or a rotating team?</li>
                  <li>What kind of stimulation protocol would you recommend for me and why?</li>
                  <li>How often would I need to come in, and do you offer early or weekend appointments?</li>
                </ul>
              </div>

              <div className="step-section">
                <h4>3. Trust your gut</h4>
                <p>Seriously — everyone in the threads said this:</p>
                <p>If something feels off, even if the stats look great, it's okay to walk away.</p>
                <p>You want a clinic that's competent and makes you feel cared for.</p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'community' && (
          <div className="content-section">
            <div className="community-intro">
              <p>Real experiences shared by the Reddit community:</p>
            </div>
            
            {isLoading && (
              <div className="loading-state">
                <p>Fetching relevant posts...</p>
              </div>
            )}
            
            {error && (
              <div className="error-state">
                <p>{error}</p>
              </div>
            )}
            
            <div className="community-posts">
              {redditPosts.map((post) => (
                <div key={post.id} className="community-post">
                  <div className="post-subreddit">{post.subreddit}</div>
                  <h4>{post.title}</h4>
                  <p>{post.content.length > 300 ? `${post.content.substring(0, 300)}...` : post.content}</p>
                  <div className="post-meta">
                    <span className="author">Posted by {post.author}</span>
                    <div className="engagement">
                      <span>👍 {post.upvotes}</span>
                      <span>💬 {post.comments}</span>
                    </div>
                  </div>
                  <a 
                    href={post.url} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="read-more-link"
                  >
                    Read full post on Reddit →
                  </a>
                </div>
              ))}
              
              {!isLoading && !error && redditPosts.length === 0 && (
                <div className="no-results">
                  <p>No relevant Reddit posts found. Try rephrasing your question.</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default QuestionAnswer; 